import { useEffect } from 'react';

// Data-entry screens are keyboard-driven, so Enter should walk to the next
// field instead of submitting the form half-filled. Saving stays on the Save
// button (Enter still works once the button itself has focus).
// Login is outside <Layout>, so Enter still signs in there.

const SKIP_INPUT_TYPES = new Set(['submit', 'button', 'reset', 'checkbox', 'radio', 'file', 'hidden', 'image']);

const isEditable = (el) => {
  if (!el || el.disabled) return false;
  const tag = el.tagName;
  if (tag === 'SELECT' || tag === 'TEXTAREA') return true;
  return tag === 'INPUT' && !SKIP_INPUT_TYPES.has(el.type);
};

const isReachable = (el) => el.offsetParent !== null && !el.readOnly && el.tabIndex !== -1;

export default function useEnterAdvancesFocus() {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Enter' || e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return; // Ctrl+Enter etc. stay as-is
      const el = e.target;
      // Textareas keep Enter for new lines; buttons/links keep their own action.
      if (!isEditable(el) || el.tagName === 'TEXTAREA') return;

      const scope = el.closest('form');
      if (!scope) return; // outside a form Enter does nothing anyway

      const fields = Array.from(scope.querySelectorAll('input, select, textarea'))
        .filter(f => isEditable(f) && isReachable(f));
      const next = fields[fields.indexOf(el) + 1];

      e.preventDefault(); // never an implicit submit
      if (next) {
        next.focus();
        if (typeof next.select === 'function' && next.tagName === 'INPUT' && next.type === 'text') next.select();
      } else {
        // Last field — hand focus to Save so the next Enter commits.
        const submit = scope.querySelector('button[type="submit"], input[type="submit"]');
        if (submit && !submit.disabled) submit.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}
